// Create a new component: ReuseJointModal.tsx
import React, { useState, useMemo } from 'react';
import { Button, Table } from '@bphxd/ds-core-react';
import { TJoint } from '../../../lib/types';
import classes from '../AspectsTable/AspectsTable.module.scss';
import { GET_JOINTS_BY_PID } from '../../../graph-queries/tar/joint';
import { useLazyQuery } from '@apollo/client';
import { Duplicate16 } from '@bphxd/ds-core-react/lib/icons';
import JointForm from '../JointForm/JointForm';

type Props = {
    onJointSelect?: (joint: TJoint) => void;
    onClose: () => void;
    certificateType: string;
    onSubmit: (joint: TJoint) => Promise<unknown>;
    isLoading?: boolean;
};

const ReuseJointModal: React.FC<Props> = ({
    onJointSelect,
    onClose,
    certificateType,
    onSubmit,
    isLoading
}) => {
    const [searchString, setSearchString] = useState('');
    const [joints, setJoints] = useState<TJoint[]>([]); 
    const [selectedJointForReuse, setSelectedJointForReuse] = useState<TJoint | null>(null);

    // Reset state when modal reopens
    React.useEffect(() => {
        setSearchString('');
        setJoints([]);
        setSelectedJointForReuse(null);
    }, []);

    // fetching joints by P&ID
    const [fetchJointsByPID, { loading, error }] = useLazyQuery(GET_JOINTS_BY_PID, {
        fetchPolicy: 'cache-and-network', // Always get fresh data
        onCompleted: (data) => {
            setJoints(
            (data.jointsbyPID || []).map((joint: any) => ({
                ...joint,
                updatedAt: joint.updatedAt ?? '', // Provide a default value if missing
            }))
            );
        },
        onError: (error) => {
            console.error('Error fetching joints by P&ID:', error);
            setJoints([]);
        }
    });

    // Handle search input change with debounce effect
    const handleSearchChange = (value: string) => {
        setSearchString(value);
        
        // Clear previous results when search is cleared
        if (!value.trim()) {
        setJoints([]);
        return;
        }
        
        // Fetch joints when user types a P&ID
        const timeoutId = setTimeout(() => {
        if (value.trim()) {
            fetchJointsByPID({
            variables: { pIdNo: value.trim() }
            });
        }
        }, 500); // 500ms debounce

        // Cleanup timeout on component unmount or search change
        return () => clearTimeout(timeoutId);
    };
    
    const handleJointSelect = (joint: TJoint) => {
        setSelectedJointForReuse(joint);
    };

    const handleFormSubmit = (jointData: TJoint) => {
        return onSubmit(jointData).then(() => {
            setSelectedJointForReuse(null);
            onClose();
        });
    };


    // If a joint is selected for reuse, show the joint form
    if (selectedJointForReuse) {
        return (
        <div>
            <JointForm
            certificateType={certificateType}
            defaultValues={selectedJointForReuse}
            onSubmit={handleFormSubmit}
            onClose={() => setSelectedJointForReuse(null)}
            type={'add'}
            isLoading={isLoading}
            />
        </div>
        );
    }

    return (
        <div>
            {/* Search Bar */}
            <div className={classes.searchWrapper} style={{ marginBottom: '1rem' }}>
                <Table.Search
                initialValue={searchString}
                className="w-100 bg-white"
                onChange={handleSearchChange}
                debounce={300}
                placeholder="Search for joints by P&ID"
                />
            </div>
        
            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                Searching for joints...
                </div>
            )}
        
            {/* Error State */}
            {error && (
            <div className="alert alert-danger" role="alert">
                Error loading joints: {error.message}
            </div>
            )}
            
            {!loading && !error && joints.length > 0 && (
                <>
                    <Table size="sm">
                        <Table.Head>
                            <Table.HeaderRow>
                                <Table.Header>Joint Number</Table.Header>
                                <Table.Header>Tag Id</Table.Header>
                                <Table.Header>P&ID No</Table.Header>
                                <Table.Header>Additional Details</Table.Header>
                                {onJointSelect && <Table.Header>Action</Table.Header>}
                            </Table.HeaderRow>
                        </Table.Head>
                        <Table.Body>
                            {joints.map((joint, idx) => (
                                <Table.Row key={joint.jointId} rowIndex={idx}>
                                    <Table.Cell>{joint.jointNumber}</Table.Cell>
                                    <Table.Cell>{joint.tagId}</Table.Cell>
                                    <Table.Cell>{joint.pIdNo}</Table.Cell>
                                    <Table.Cell>{joint.additionalDetails}</Table.Cell>
                                    <Table.Cell>
                                        <Button
                                            size="sm"
                                            level="primary"
                                            onClick={() => handleJointSelect(joint)}
                                        >
                                            <Duplicate16 /> Copy
                                        </Button>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </>
            )}

            {!loading && !error && searchString && joints.length === 0 && (
                <div className="text-center py-4 text-muted">
                {`No joints found for P&ID "${searchString}"`}
                </div>
            )}
        
        </div>
    );
};

export default ReuseJointModal;